import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import FaIcon from './FaIcon';
import { Skeleton, SkeletonCircle } from './Skeleton';
import { MondialiSectionPageShell, useIsInsideMondialiShell } from './MondialiHomePage';
import {
  MONDIALI_FINALE_PATH,
  MONDIALI_TOP_PLAYER_PATH,
  getMondialiWeekLabel,
  getMondialiWeekPath,
  getMondialiWeekWindows,
  resolveMondialiFinaleRedirectPath,
  resolveMondialiTopPlayerRedirectPath,
  resolveMondialiWeekRedirectPath,
} from '../utils/mondialiRouting';
import { getMondialiFinaleLeaderboard, getMondialiLeaderboardForRange, getMondialiTopPlayerLeaderboard } from '../api/mondiali';
import {
  buildDemoFinalePayload,
  buildDemoRangeLeaderboardPayload,
  buildDemoTopPlayerLeaderboardPayload,
  hasRangeEnded,
} from '../utils/mondialiPreviewData';
import { EXTRA_PRIZES, FINAL_PRIZES, TOP_PLAYER_PRIZE_GROUP, WEEKLY_PRIZES, FinalPodiumTier, StakanovistiTier, WeeklyPodiumTier, findAwardByPlaceNumber } from './MondialiPremiPage';
import styles from '../styles/Mondiali.module.css';

const formatScoreValue = (value, { decimals = 1 } = {}) => {
  const safeValue = Number(value);
  if (!Number.isFinite(safeValue)) {
    return decimals === 0 ? '0' : '0,0';
  }

  return new Intl.NumberFormat('it-IT', {
    useGrouping: true,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(safeValue);
};

const WEEKLY_LEADERBOARD_INELIGIBLE_MARKER = '*';
const WEEKLY_LEADERBOARD_NOTE_ID = 'weekly-leaderboard-eligibility-note';
const FINALE_TEAM_DETAILS_ID_PREFIX = 'final-team';
const STAKANOVISTI_PRIZE_GROUP = EXTRA_PRIZES.find((group) => group.key === 'stacanovisti') || null;

const getMedalClassName = (place) => {
  if (place === 1) return styles.leaderboardPlaceMedalGold;
  if (place === 2) return styles.leaderboardPlaceMedalSilver;
  if (place === 3) return styles.leaderboardPlaceMedalBronze;
  return styles.leaderboardPlaceMedalNeutral;
};

const getLeaderboardRowClassName = (place) => {
  if (place === 1) return styles.leaderboardRowFirst;
  if (place === 2) return styles.leaderboardRowSecond;
  if (place === 3) return styles.leaderboardRowThird;
  return '';
};

const sortPlayersByScore = (players = []) =>
  [...players].sort((left, right) => {
    const scoreDiff = Number(right.stelline_total) - Number(left.stelline_total);
    if (scoreDiff !== 0) {
      return scoreDiff;
    }
    return String(left.username).localeCompare(String(right.username), 'it', { sensitivity: 'base' });
  });

const isWeeklyLeaderboardCountryEligible = (country) => country?.is_weekly_leaderboard_eligible !== false;

const FlagCountryLabel = ({ country, labelClassName = '' }) => (
  <span className={styles.leaderboardCountryLabel}>
    <span className={styles.flagFrame} aria-hidden="true">
      <span className={`fi fi-${country.country_alpha2.toLowerCase()} ${styles.flag}`} />
    </span>
    <span className={labelClassName}>{country.country_name_it}</span>
  </span>
);

const FlagUsernameLabel = ({ player, labelClassName = '' }) => (
  <span className={styles.leaderboardCountryLabel}>
    <span className={styles.flagFrame} aria-hidden="true">
      <span className={`fi fi-${player.country_alpha2.toLowerCase()} ${styles.flag}`} />
    </span>
    <span className={labelClassName}>{player.username}</span>
  </span>
);

const LeaderboardPodiumWinner = ({ winner }) => (
  <span className={styles.weeklyPodiumBaseWinner}>
    <FlagUsernameLabel player={winner} labelClassName={styles.weeklyPodiumBaseWinnerName} />
  </span>
);

const LeaderboardPrizePreview = ({ mode, weekNumber = null, podium = null }) => {
  if (mode === 'weekly') {
    const weeklyPrize = WEEKLY_PRIZES.find((week) => week.weekNumber === weekNumber);
    if (!weeklyPrize) {
      return null;
    }

    const winnerByPlace = new Map((podium || []).map((winner) => [Number(winner.place), winner]));

    return (
      <div className={styles.weeklyOlympicPodium}>
        <WeeklyPodiumTier
          label="2° Premio"
          prize={weeklyPrize.second}
          standClassName={styles.weeklyPodiumTierSecond}
          placeNumber={2}
          baseContent={winnerByPlace.has(2) ? <LeaderboardPodiumWinner winner={winnerByPlace.get(2)} /> : null}
          baseClassName={winnerByPlace.has(2) ? styles.weeklyPodiumBaseHasWinner : ''}
          baseIsDecorative={!winnerByPlace.has(2)}
        />
        <WeeklyPodiumTier
          label="1° Premio"
          prize={weeklyPrize.first}
          standClassName={styles.weeklyPodiumTierFirst}
          placeNumber={1}
          baseContent={winnerByPlace.has(1) ? <LeaderboardPodiumWinner winner={winnerByPlace.get(1)} /> : null}
          baseClassName={winnerByPlace.has(1) ? styles.weeklyPodiumBaseHasWinner : ''}
          baseIsDecorative={!winnerByPlace.has(1)}
        />
        <WeeklyPodiumTier
          label="3° Premio"
          prize={weeklyPrize.third}
          standClassName={styles.weeklyPodiumTierThird}
          placeNumber={3}
          baseContent={winnerByPlace.has(3) ? <LeaderboardPodiumWinner winner={winnerByPlace.get(3)} /> : null}
          baseClassName={winnerByPlace.has(3) ? styles.weeklyPodiumBaseHasWinner : ''}
          baseIsDecorative={!winnerByPlace.has(3)}
        />
      </div>
    );
  }

  if (mode === 'finale') {
    const winnerByPlace = new Map((podium || []).map((winner) => [Number(winner.place), winner]));

    return (
      <div className={`${styles.weeklyOlympicPodium} ${styles.finalOlympicPodium}`}>
        <FinalPodiumTier
          placeNumber={2}
          label="2° posto finale"
          prizes={FINAL_PRIZES[1].prizes}
          standClassName={styles.weeklyPodiumTierSecond}
          baseContent={winnerByPlace.has(2) ? <LeaderboardPodiumWinner winner={winnerByPlace.get(2)} /> : null}
          baseClassName={winnerByPlace.has(2) ? styles.weeklyPodiumBaseHasWinner : ''}
          baseIsDecorative={!winnerByPlace.has(2)}
        />
        <FinalPodiumTier
          placeNumber={1}
          label="1° posto finale"
          prizes={FINAL_PRIZES[0].prizes}
          standClassName={styles.weeklyPodiumTierFirst}
          baseContent={winnerByPlace.has(1) ? <LeaderboardPodiumWinner winner={winnerByPlace.get(1)} /> : null}
          baseClassName={winnerByPlace.has(1) ? styles.weeklyPodiumBaseHasWinner : ''}
          baseIsDecorative={!winnerByPlace.has(1)}
        />
        <FinalPodiumTier
          placeNumber={3}
          label="3° posto finale"
          prizes={FINAL_PRIZES[2].prizes}
          standClassName={styles.weeklyPodiumTierThird}
          baseContent={winnerByPlace.has(3) ? <LeaderboardPodiumWinner winner={winnerByPlace.get(3)} /> : null}
          baseClassName={winnerByPlace.has(3) ? styles.weeklyPodiumBaseHasWinner : ''}
          baseIsDecorative={!winnerByPlace.has(3)}
        />
      </div>
    );
  }

  if (mode === 'stacanovisti' && STAKANOVISTI_PRIZE_GROUP?.awards?.[0]) {
    const repeatedPrize = STAKANOVISTI_PRIZE_GROUP.awards[0];
    const winnerByPlace = new Map((podium || []).map((winner) => [Number(winner.place), winner]));

    return (
      <div className={`${styles.weeklyOlympicPodium} ${styles.stakanovistiTrio}`}>
        <StakanovistiTier
          prize={repeatedPrize}
          slot={1}
          baseContent={winnerByPlace.has(1) ? <LeaderboardPodiumWinner winner={winnerByPlace.get(1)} /> : null}
          baseClassName={winnerByPlace.has(1) ? styles.weeklyPodiumBaseHasWinner : ''}
          baseIsDecorative={!winnerByPlace.has(1)}
        />
        <StakanovistiTier
          prize={repeatedPrize}
          slot={2}
          baseContent={winnerByPlace.has(2) ? <LeaderboardPodiumWinner winner={winnerByPlace.get(2)} /> : null}
          baseClassName={winnerByPlace.has(2) ? styles.weeklyPodiumBaseHasWinner : ''}
          baseIsDecorative={!winnerByPlace.has(2)}
        />
        <StakanovistiTier
          prize={repeatedPrize}
          slot={3}
          baseContent={winnerByPlace.has(3) ? <LeaderboardPodiumWinner winner={winnerByPlace.get(3)} /> : null}
          baseClassName={winnerByPlace.has(3) ? styles.weeklyPodiumBaseHasWinner : ''}
          baseIsDecorative={!winnerByPlace.has(3)}
        />
      </div>
    );
  }

  if (mode === 'top-player') {
    const winnerByPlace = new Map((podium || []).map((winner) => [Number(winner.place), winner]));

    return (
      <div className={`${styles.weeklyOlympicPodium} ${styles.extraTopPlayersPodium}`}>
        <WeeklyPodiumTier
          label="2° posto top player"
          prize={findAwardByPlaceNumber(TOP_PLAYER_PRIZE_GROUP.awards, 2)}
          standClassName={styles.weeklyPodiumTierSecond}
          placeNumber={2}
          baseContent={winnerByPlace.has(2) ? <LeaderboardPodiumWinner winner={winnerByPlace.get(2)} /> : null}
          baseClassName={winnerByPlace.has(2) ? styles.weeklyPodiumBaseHasWinner : ''}
          baseIsDecorative={!winnerByPlace.has(2)}
        />
        <WeeklyPodiumTier
          label="1° posto top player"
          prize={findAwardByPlaceNumber(TOP_PLAYER_PRIZE_GROUP.awards, 1)}
          standClassName={styles.weeklyPodiumTierFirst}
          placeNumber={1}
          baseContent={winnerByPlace.has(1) ? <LeaderboardPodiumWinner winner={winnerByPlace.get(1)} /> : null}
          baseClassName={winnerByPlace.has(1) ? styles.weeklyPodiumBaseHasWinner : ''}
          baseIsDecorative={!winnerByPlace.has(1)}
        />
        <WeeklyPodiumTier
          label="3° posto top player"
          prize={findAwardByPlaceNumber(TOP_PLAYER_PRIZE_GROUP.awards, 3)}
          standClassName={styles.weeklyPodiumTierThird}
          placeNumber={3}
          baseContent={winnerByPlace.has(3) ? <LeaderboardPodiumWinner winner={winnerByPlace.get(3)} /> : null}
          baseClassName={winnerByPlace.has(3) ? styles.weeklyPodiumBaseHasWinner : ''}
          baseIsDecorative={!winnerByPlace.has(3)}
        />
      </div>
    );
  }

  return null;
};

const LeaderboardRows = ({
  countries,
  scoreField,
  detailsIdPrefix = 'leaderboard-country',
  scoreDecimals = 1,
}) => {
  const DETAIL_SCROLL_OFFSET_PX = 110;

  const handleRowClick = (country) => {
    const anchor = document.getElementById(`${detailsIdPrefix}-${country.country_alpha2.toLowerCase()}`);
    if (!anchor) {
      return;
    }

    const anchorTop = anchor.getBoundingClientRect().top + window.scrollY;
    const targetTop = Math.max(0, anchorTop - DETAIL_SCROLL_OFFSET_PX);
    window.scrollTo({
      top: targetTop,
      behavior: 'smooth',
    });
  };

  return (
    <ol className={styles.leaderboardRowsList}>
      {countries.map((country) => {
        const score = formatScoreValue(country[scoreField], { decimals: scoreDecimals });
        const place = Number(country.place) || 0;

        return (
          <li key={`leaderboard-row-${country.country_alpha2}`}>
            <button
              type="button"
              className={`${styles.leaderboardRowButton} ${getLeaderboardRowClassName(place)}`}
              onClick={() => handleRowClick(country)}
            >
              <span className={styles.leaderboardRowLeft}>
                {place <= 3 ? (
                  <span
                    className={`${styles.leaderboardPlaceMedal} ${getMedalClassName(place)}`}
                    aria-label={`${place}° posto`}
                  >
                    <span className={styles.weeklyPodiumRibbon} aria-hidden="true" />
                    <span className={styles.weeklyPodiumMedalBadge} aria-hidden="true">{place}</span>
                  </span>
                ) : (
                  <span className={`${styles.leaderboardPlaceText} ${getMedalClassName(place)}`}>
                    {place}°
                  </span>
                )}
                <FlagCountryLabel
                  country={country}
                  labelClassName={place <= 3 ? styles.leaderboardCountryNameTop3 : ''}
                />
              </span>
              <span className={styles.leaderboardRowScore}>
                <span>{score}</span>
                <FaIcon className={`fa fa-star ${styles.leaderboardScoreStar}`} aria-hidden="true" />
              </span>
            </button>
          </li>
        );
      })}
    </ol>
  );
};

const LeaderboardRowsSkeleton = () => (
  <ol className={styles.leaderboardRowsList} aria-hidden="true">
    {Array.from({ length: 8 }).map((_, index) => (
      <li key={`leaderboard-skeleton-row-${index}`}>
        <div className={`${styles.leaderboardRowButton} ${styles.leaderboardRowSkeleton}`}>
          <span className={styles.leaderboardRowLeft}>
            <SkeletonCircle size={24} />
            <Skeleton
              width={index % 3 === 0 ? '42%' : index % 3 === 1 ? '36%' : '48%'}
              height="0.95rem"
              borderRadius="6px"
            />
          </span>
          <Skeleton width="4.5rem" height="0.95rem" borderRadius="6px" />
        </div>
      </li>
    ))}
  </ol>
);

const TopPlayerRows = ({ players }) => (
  <ol className={styles.leaderboardRowsList}>
    {players.map((player) => {
      const place = Number(player.place) || 0;
      const scoreClassName = place <= 3
        ? `${styles.leaderboardRowScore} ${styles.topPlayerRowScoreTop3}`
        : `${styles.leaderboardRowScore} ${styles.topPlayerRowScoreStandard}`;

      return (
        <li key={`top-player-row-${player.username}-${player.country_alpha2}`}>
          <div className={`${styles.leaderboardRowButton} ${styles.leaderboardRowStatic} ${getLeaderboardRowClassName(place)}`}>
            <span className={styles.leaderboardRowLeft}>
              {place <= 3 ? (
                <span
                  className={`${styles.leaderboardPlaceMedal} ${getMedalClassName(place)}`}
                  aria-label={`${place}° posto`}
                >
                  <span className={styles.weeklyPodiumRibbon} aria-hidden="true" />
                  <span className={styles.weeklyPodiumMedalBadge} aria-hidden="true">{place}</span>
                </span>
              ) : (
                <span className={`${styles.leaderboardPlaceText} ${getMedalClassName(place)}`}>
                  {place}°
                </span>
              )}
              <FlagUsernameLabel
                player={player}
                labelClassName={place <= 3 ? styles.leaderboardCountryNameTop3 : ''}
              />
            </span>
            <span className={scoreClassName}>
              <span>{formatScoreValue(player.stelline_total, { decimals: 0 })}</span>
              <FaIcon className={`fa fa-star ${styles.leaderboardScoreStar}`} aria-hidden="true" />
            </span>
          </div>
        </li>
      );
    })}
  </ol>
);

const FinaleLeaderboardRows = ({ countries, detailsIdPrefix = FINALE_TEAM_DETAILS_ID_PREFIX }) => {
  const DETAIL_SCROLL_OFFSET_PX = 110;

  const handleRowClick = (country) => {
    const anchor = document.getElementById(`${detailsIdPrefix}-${country.country_alpha2.toLowerCase()}`);
    if (!anchor) {
      return;
    }

    const anchorTop = anchor.getBoundingClientRect().top + window.scrollY;
    const targetTop = Math.max(0, anchorTop - DETAIL_SCROLL_OFFSET_PX);
    window.scrollTo({
      top: targetTop,
      behavior: 'smooth',
    });
  };

  if (countries.length === 0) {
    return <p className={styles.leaderboardNoPlayersText}>Nessuna squadra si è qualificata per la classifica finale.</p>;
  }

  return (
    <div className={styles.finaleTableScroller}>
        <div className={styles.finaleLeaderboardTable}>
          <div className={styles.finaleLeaderboardHeader} aria-hidden="true">
            <span className={styles.finaleLeaderboardTeamHeader}>Squadra</span>
          <span className={styles.finaleLeaderboardStatHeader}>S1</span>
          <span className={styles.finaleLeaderboardStatHeader}>S2</span>
          <span className={styles.finaleLeaderboardStatHeader}>S3</span>
          <span className={styles.finaleLeaderboardStatHeader}>S4</span>
          <span className={styles.finaleLeaderboardStatHeader}>S5</span>
          <span className={styles.finaleLeaderboardStatHeader}>Media</span>
          <span className={styles.finaleLeaderboardTotalHeader}>Stelline</span>
          </div>

        <ol className={styles.finaleLeaderboardRowsList}>
          {countries.map((country) => {
            const place = Number(country.place) || 0;
            const placementsByWeek = new Map(
              (country.weekly_placements || []).map((placement) => [Number(placement.week_number), Number(placement.place)]),
            );

            return (
              <li key={`finale-row-${country.country_alpha2}`}>
                <button
                  type="button"
                  className={`${styles.finaleLeaderboardRowButton} ${getLeaderboardRowClassName(place)}`}
                  onClick={() => handleRowClick(country)}
                >
                  <span className={styles.finaleLeaderboardTeamCell}>
                    {place <= 3 ? (
                      <span
                        className={`${styles.leaderboardPlaceMedal} ${getMedalClassName(place)}`}
                        aria-label={`${place}° posto`}
                      >
                        <span className={styles.weeklyPodiumRibbon} aria-hidden="true" />
                        <span className={styles.weeklyPodiumMedalBadge} aria-hidden="true">{place}</span>
                      </span>
                    ) : (
                      <span className={`${styles.leaderboardPlaceText} ${getMedalClassName(place)}`}>
                        {place}°
                      </span>
                    )}
                    <FlagCountryLabel
                      country={country}
                      labelClassName={place <= 3 ? styles.leaderboardCountryNameTop3 : ''}
                    />
                  </span>
                  {Array.from({ length: 5 }, (_, index) => (
                    <span key={`${country.country_alpha2}-week-${index + 1}`} className={styles.finaleLeaderboardStatCell}>
                      {placementsByWeek.get(index + 1) || '-'}
                    </span>
                  ))}
                  <span className={`${styles.finaleLeaderboardStatCell} ${styles.finaleLeaderboardAverageCell}`}>
                    {formatScoreValue(country.average_weekly_placement, { decimals: 1 })}
                  </span>
                  <span className={`${styles.finaleLeaderboardStatCell} ${styles.finaleLeaderboardTotalCell}`}>
                    {formatScoreValue(country.competition_stelline_total, { decimals: 0 })} <FaIcon className={`fa fa-star ${styles.leaderboardScoreStar}`} aria-hidden="true" />
                  </span>
                </button>
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
};

const FinaleLeaderboardRowsSkeleton = () => (
  <div className={styles.finaleTableScroller} aria-hidden="true">
    <div className={styles.finaleLeaderboardTable}>
      <div className={styles.finaleLeaderboardHeader}>
        <span className={styles.finaleLeaderboardTeamHeader}>Squadra</span>
        <span className={styles.finaleLeaderboardStatHeader}>S1</span>
        <span className={styles.finaleLeaderboardStatHeader}>S2</span>
        <span className={styles.finaleLeaderboardStatHeader}>S3</span>
        <span className={styles.finaleLeaderboardStatHeader}>S4</span>
        <span className={styles.finaleLeaderboardStatHeader}>S5</span>
        <span className={styles.finaleLeaderboardStatHeader}>Media</span>
        <span className={styles.finaleLeaderboardTotalHeader}>Stelline</span>
      </div>
      <div className={styles.finaleLeaderboardRowsList}>
        {Array.from({ length: 5 }).map((_, index) => (
          <div key={`finale-row-skeleton-${index}`} className={styles.finaleLeaderboardRowSkeleton}>
            <span className={styles.finaleLeaderboardTeamCell}>
              <SkeletonCircle size={24} />
              <Skeleton width={index % 2 === 0 ? '8rem' : '9rem'} height="0.95rem" borderRadius="6px" />
            </span>
            {Array.from({ length: 7 }).map((__, statIndex) => (
              <Skeleton
                key={`finale-row-skeleton-${index}-${statIndex}`}
                width={statIndex === 6 ? '4rem' : '2rem'}
                height="0.9rem"
                borderRadius="6px"
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  </div>
);

const LeaderboardCountryDetails = ({
  countries,
  scoreField,
  detailsIdPrefix = 'leaderboard-country',
  scoreDecimals = 1,
}) => {
  const DETAIL_SCROLL_OFFSET_PX = 110;
  const alphabeticalCountries = useMemo(
    () => [...countries].sort((left, right) => left.country_name_it.localeCompare(right.country_name_it, 'it', { sensitivity: 'base' })),
    [countries],
  );

  const handleEligibilityMarkerClick = () => {
    const anchor = document.getElementById(WEEKLY_LEADERBOARD_NOTE_ID);
    if (!anchor) {
      return;
    }

    const anchorTop = anchor.getBoundingClientRect().top + window.scrollY;
    const targetTop = Math.max(0, anchorTop - DETAIL_SCROLL_OFFSET_PX);
    window.scrollTo({
      top: targetTop,
      behavior: 'smooth',
    });
  };

  return (
    <section className={styles.leaderboardCountryDetailsSection} aria-label="Dettaglio squadre">
      {alphabeticalCountries.map((country) => {
        const players = sortPlayersByScore(country.players || []);
        const detailsId = `${detailsIdPrefix}-${country.country_alpha2.toLowerCase()}`;
        const isWeeklyEligible = isWeeklyLeaderboardCountryEligible(country);
        const countryPlace = isWeeklyEligible
          ? `${country.place}°`
          : WEEKLY_LEADERBOARD_INELIGIBLE_MARKER;
        const shouldHideWeeklyScore = scoreField === 'average_top11_stelline' && !isWeeklyEligible;

        return (
          <article id={detailsId} key={`country-details-${country.country_alpha2}`} className={styles.leaderboardCountryDetailsCard}>
            <h3 className={styles.leaderboardCountryDetailsTitle}>
              <span className={styles.leaderboardCountryIdentity}>
                <FlagCountryLabel country={country} />
                {isWeeklyEligible ? (
                  <span className={styles.leaderboardCountryPlace}>{countryPlace}</span>
                ) : (
                  <button
                    type="button"
                    className={styles.leaderboardCountryPlaceIneligibleButton}
                    title="Squadra non idonea alla classifica settimanale"
                    aria-label="Squadra non idonea alla classifica settimanale"
                    onClick={handleEligibilityMarkerClick}
                  >
                    <span className={`${styles.leaderboardCountryPlace} ${styles.leaderboardCountryPlaceIneligible}`}>
                      {countryPlace}
                    </span>
                  </button>
                )}
              </span>
              {shouldHideWeeklyScore ? null : (
                <span className={styles.leaderboardCountryDetailsScore}>
                  {formatScoreValue(country[scoreField], { decimals: scoreDecimals })} <FaIcon className={`fa fa-star ${styles.leaderboardScoreStar}`} aria-hidden="true" />
                </span>
              )}
            </h3>

            {players.length > 0 ? (
              <ol className={styles.leaderboardPlayerList}>
                {players.map((player, index) => {
                  const rank = index + 1;
                  const isTop11 = rank <= 11;

                  return (
                    <li
                      key={`${country.country_alpha2}-${player.username}-${rank}`}
                      className={`${styles.leaderboardPlayerItem} ${isTop11 ? styles.leaderboardPlayerItemTop11 : ''}`}
                    >
                      <span className={styles.leaderboardPlayerRank}>{rank}</span>
                      <span className={styles.leaderboardPlayerName}>{player.username}</span>
                      <span className={styles.leaderboardPlayerScore}>
                        {formatScoreValue(player.stelline_total, { decimals: 0 })}
                      </span>
                    </li>
                  );
                })}
              </ol>
            ) : (
              <p className={styles.leaderboardNoPlayersText}>Nessun giocatore di questa squadra ha ancora ottenuto stelline.</p>
            )}
          </article>
        );
      })}
    </section>
  );
};

const LeaderboardCountryDetailsSkeleton = () => (
  <section className={styles.leaderboardCountryDetailsSection} aria-hidden="true">
    {Array.from({ length: 5 }).map((_, cardIndex) => (
      <article key={`leaderboard-details-skeleton-${cardIndex}`} className={styles.leaderboardCountryDetailsCard}>
        <div className={styles.leaderboardCountryDetailsSkeletonHeader}>
          <span className={styles.leaderboardCountryIdentity}>
            <SkeletonCircle size={20} />
            <Skeleton width={cardIndex % 2 === 0 ? '7rem' : '8.5rem'} height="0.95rem" borderRadius="6px" />
            <Skeleton width="1.4rem" height="0.9rem" borderRadius="6px" />
          </span>
          <Skeleton width="4rem" height="0.95rem" borderRadius="6px" />
        </div>

        <div className={styles.leaderboardPlayerSkeletonList}>
          {Array.from({ length: 7 }).map((__, playerIndex) => (
            <div key={`leaderboard-player-skeleton-${cardIndex}-${playerIndex}`} className={styles.leaderboardPlayerSkeletonRow}>
              <Skeleton width="1.3rem" height="0.78rem" borderRadius="6px" />
              <Skeleton
                width={playerIndex % 3 === 0 ? '64%' : playerIndex % 3 === 1 ? '76%' : '58%'}
                height="0.88rem"
                borderRadius="6px"
              />
              <Skeleton width="2.2rem" height="0.85rem" borderRadius="6px" />
            </div>
          ))}
        </div>
      </article>
    ))}
  </section>
);

const FinaleTeamDetails = ({
  teamDetails,
  detailsIdPrefix = FINALE_TEAM_DETAILS_ID_PREFIX,
}) => (
  <section className={styles.leaderboardCountryDetailsSection} aria-label="Dettaglio punteggi squadre">
    {teamDetails.map((team) => (
      <article
        id={`${detailsIdPrefix}-${team.country_alpha2.toLowerCase()}`}
        key={`final-team-details-${team.country_alpha2}`}
        className={styles.leaderboardCountryDetailsCard}
      >
        <h3 className={styles.leaderboardCountryDetailsTitle}>
          <span className={styles.leaderboardCountryIdentity}>
            <FlagCountryLabel country={team} />
            {team.is_final_leaderboard_qualified && Number.isInteger(team.final_place) ? (
              <span className={styles.leaderboardCountryPlace}>{team.final_place}°</span>
            ) : null}
          </span>
          <span className={styles.leaderboardCountryDetailsScore}>
            {formatScoreValue(team.competition_stelline_total, { decimals: 0 })} <FaIcon className={`fa fa-star ${styles.leaderboardScoreStar}`} aria-hidden="true" />
          </span>
        </h3>

        {team.players.length > 0 ? (
          <ol className={styles.leaderboardPlayerList}>
            {team.players.map((player, index) => (
              <li
                key={`${team.country_alpha2}-${player.username}-${index + 1}`}
                className={styles.leaderboardPlayerItem}
              >
                <span className={styles.leaderboardPlayerRank}>{index + 1}</span>
                <span className={styles.leaderboardPlayerName}>{player.username}</span>
                <span className={styles.leaderboardPlayerScore}>
                  {formatScoreValue(player.competition_stelline_total, { decimals: 0 })}
                </span>
              </li>
            ))}
          </ol>
        ) : (
          <p className={styles.leaderboardNoPlayersText}>Nessun giocatore di questa squadra ha ottenuto stelline durante la competizione.</p>
        )}
      </article>
    ))}
  </section>
);

const FinaleLeaderboardCard = ({
  isLoading,
  error,
  errorMessage = 'Impossibile caricare la classifica finale. Riprova tra poco.',
  countries,
  podium = null,
  teamDetails,
  stacanovistiPodium = null,
}) => {
  if (error && !isLoading) {
    return (
      <section className={styles.card}>
        <h2 className={styles.sectionTitle}>Classifica Finale</h2>
        <p className={styles.errorBanner}>{errorMessage}</p>
      </section>
    );
  }

  return (
    <section className={styles.card}>
      <h2 className={styles.sectionTitle}>Classifica Finale</h2>

      <section className={styles.leaderboardBlockSection} aria-label="Premi in palio">
        <h3 className={styles.leaderboardBlockTitle}>Premi in palio</h3>
        <p className={styles.leaderboardBlockDescription}>
          I premi finali sono assegnati al primo giocatore della prima, della seconda e della terza squadra nella classifica finale. Primo giocatore significa il giocatore che ha ottenuto più stelline all’interno di ognuna delle squadre.
        </p>
        <div className={styles.leaderboardPrizesStrip}>
          <LeaderboardPrizePreview mode="finale" podium={podium} />
        </div>
      </section>

      <section className={styles.leaderboardBlockSection} aria-label="Classifica squadre">
        <h3 className={styles.leaderboardBlockTitle}>Classifica squadre</h3>
        <p className={styles.leaderboardBlockDescription}>
          La classifica finale ordina le squadre in base alla media dei piazzamenti settimanali: più la media è bassa, migliore è il risultato. Le colonne S1-S5 mostrano i piazzamenti delle cinque settimane; l'ultima colonna con la stellina mostra il totale stelline della squadra durante tutta la competizione.
        </p>
        {isLoading ? <FinaleLeaderboardRowsSkeleton /> : <FinaleLeaderboardRows countries={countries} />}
      </section>

      <section className={styles.leaderboardBlockSection} aria-label="Stacanovisti">
        <h3 className={styles.leaderboardBlockTitle}>Stacanovisti</h3>
        <p className={styles.leaderboardBlockDescription}>
          I premi stacanovisti vanno ai primi tre giocatori della squadra vincitrice che hanno partecipato tutti i giorni alla competizione; in tal senso, contano sia il numero di giorni di gioco sia il numero di stelline ottenute.
        </p>
        <div className={styles.leaderboardPrizesStrip}>
          <LeaderboardPrizePreview mode="stacanovisti" podium={stacanovistiPodium} />
        </div>
      </section>

      <section className={styles.leaderboardBlockSection} aria-label="Dettaglio punteggi squadre">
        <h3 className={styles.leaderboardBlockTitle}>Dettaglio punteggi squadre</h3>
        <p className={styles.leaderboardBlockDescription}>
          Tutte le squadre sono elencate in ordine alfabetico secondo il nome italiano. Ciascuna scheda mostra il totale stelline della squadra e i punteggi individuali di tutti i giocatori durante l'intera competizione.
        </p>
        {isLoading ? <LeaderboardCountryDetailsSkeleton /> : <FinaleTeamDetails teamDetails={teamDetails} />}
      </section>
    </section>
  );
};

const LeaderboardCard = ({
  title,
  isLoading,
  error,
  errorMessage,
  countries,
  podium = null,
  scoreField,
  prizesMode = null,
  weekNumber = null,
  detailsIdPrefix,
}) => {
  if (error && !isLoading) {
    return (
      <section className={styles.card}>
        <h2 className={styles.sectionTitle}>{title}</h2>
        <p className={styles.errorBanner}>{errorMessage}</p>
      </section>
    );
  }

  const scoreMetricLabel = scoreField === 'average_weekly_placement'
    ? 'media dei piazzamenti settimanali'
    : 'media stelline delle Top 11';
  const scoreDecimals = scoreField === 'average_top11_stelline' ? 0 : 1;
  const rankedCountries = scoreField === 'average_top11_stelline'
    ? countries.filter((country) => isWeeklyLeaderboardCountryEligible(country))
    : countries;
  const hasIneligibleWeeklyCountries = scoreField === 'average_top11_stelline'
    && countries.some((country) => !isWeeklyLeaderboardCountryEligible(country));
  const detailsDescription = `Schede in ordine alfabetico con indicazione del posto in classifica, della ${scoreMetricLabel} e dei punteggi dei singoli giocatori.`;

  return (
    <section className={styles.card}>
      <h2 className={styles.sectionTitle}>{title}</h2>
      {prizesMode ? (
        <section className={styles.leaderboardBlockSection} aria-label="Premi in palio">
          <h3 className={styles.leaderboardBlockTitle}>Premi in palio</h3>
          <p className={styles.leaderboardBlockDescription}>
            Il premio va al giocatore che ha ottenuto più stelline di ciascuna delle prime tre squadre classificate. Per valorizzare il contributo di tutti, se un giocatore ha già vinto un premio settimanale, il premio della sua squadra passa al primo giocatore in classifica che non ha ancora vinto premi settimanali.
          </p>
          <div className={styles.leaderboardPrizesStrip}>
            <LeaderboardPrizePreview mode={prizesMode} weekNumber={weekNumber} podium={podium} />
          </div>
        </section>
      ) : null}

      <section className={styles.leaderboardBlockSection} aria-label="Classifica squadre">
        <h3 className={styles.leaderboardBlockTitle}>Classifica squadre</h3>
        <p className={styles.leaderboardBlockDescription}>
          Il punteggio indica la {scoreMetricLabel} di ogni squadra. Clicca sul nome della nazionale per vedere i punteggi dei singoli giocatori.
          {scoreField === 'average_top11_stelline' ? ' Sono mostrate solo le squadre con almeno 5 giocatori attivi.' : ''}
        </p>
        {isLoading ? (
          <LeaderboardRowsSkeleton />
        ) : (
          <LeaderboardRows
            countries={rankedCountries}
            scoreField={scoreField}
            detailsIdPrefix={detailsIdPrefix}
            scoreDecimals={scoreDecimals}
          />
        )}
      </section>

      <section className={styles.leaderboardBlockSection} aria-label="Dettaglio punteggi squadre">
        <h3 className={styles.leaderboardBlockTitle}>Dettaglio punteggi squadre</h3>
        <p className={styles.leaderboardBlockDescription}>
          {detailsDescription}
        </p>
        {isLoading ? (
          <LeaderboardCountryDetailsSkeleton />
        ) : (
          <LeaderboardCountryDetails
            countries={countries}
            scoreField={scoreField}
            detailsIdPrefix={detailsIdPrefix}
            scoreDecimals={scoreDecimals}
          />
        )}
      </section>

      {hasIneligibleWeeklyCountries ? (
        <section
          id={WEEKLY_LEADERBOARD_NOTE_ID}
          className={`${styles.leaderboardBlockSection} ${styles.leaderboardEligibilityNoteSection}`}
          aria-label="Nota idoneità classifica settimanale"
        >
          <div className={styles.leaderboardEligibilityFootnote}>
            <span
              className={`${styles.leaderboardCountryPlace} ${styles.leaderboardCountryPlaceIneligible}`}
              aria-hidden="true"
            >
              {WEEKLY_LEADERBOARD_INELIGIBLE_MARKER}
            </span>
            <p className={styles.leaderboardBlockDescription}>
              Le squadre contrassegnate da questo simbolo non sono incluse nella classifica settimanale perché hanno meno di 5 giocatori registrati. Per loro non sono previsti né piazzamento né media stelline. Se vuoi far entrare la tua nazionale in classifica, invita i tuoi amici ad unirsi alla tua squadra.
            </p>
          </div>
        </section>
      ) : null}
    </section>
  );
};

const TopPlayerLeaderboardCard = ({
  isLoading,
  error,
  errorMessage = 'Impossibile caricare la classifica Top Player. Riprova tra poco.',
  players,
  podium = null,
}) => {
  if (error && !isLoading) {
    return (
      <section className={styles.card}>
        <h2 className={styles.sectionTitle}>Top Player</h2>
        <p className={styles.errorBanner}>{errorMessage}</p>
      </section>
    );
  }

  return (
    <section className={styles.card}>
      <h2 className={styles.sectionTitle}>Top Player</h2>

      {TOP_PLAYER_PRIZE_GROUP ? (
        <section className={styles.leaderboardBlockSection} aria-label="Premi in palio">
          <h3 className={styles.leaderboardBlockTitle}>Premi in palio</h3>
          <p className={styles.leaderboardBlockDescription}>
            I seguenti premi sono assegnati ai 3 giocatori con più stelline in assoluto al termine della competizione, alle 00:00 UTC del 19 luglio.
          </p>
          <div className={styles.leaderboardPrizesStrip}>
            <LeaderboardPrizePreview mode="top-player" podium={podium} />
          </div>
        </section>
      ) : null}

      <section className={styles.leaderboardBlockSection} aria-label="Classifica individuale">
        <h3 className={styles.leaderboardBlockTitle}>Classifica individuale</h3>
        <p className={styles.leaderboardBlockDescription}>
          La classifica somma tutte le stelline ottenute durante i Mondiali, dalle 00:00 UTC del 14 giugno alle 00:00 UTC del 19 luglio.
        </p>
        {isLoading ? <LeaderboardRowsSkeleton /> : (
          players.length > 0 ? <TopPlayerRows players={players} /> : <p className={styles.leaderboardNoPlayersText}>Nessun giocatore ha ancora ottenuto stelline.</p>
        )}
      </section>
    </section>
  );
};

const loadTopPlayerLeaderboardPayload = async ({
  startAt,
  endAt,
  isDemoMode,
  onSuccess,
  onError,
  onSettled,
}) => {
  if (isDemoMode) {
    onSuccess(buildDemoTopPlayerLeaderboardPayload({ startAt, endAt, now: new Date(endAt) }));
    onSettled();
    return;
  }

  try {
    const response = await getMondialiTopPlayerLeaderboard({
      startAt,
      endAt,
    });
    onSuccess(response);
  } catch (_) {
    onError();
  } finally {
    onSettled();
  }
};

const MondialiWeeklyLeaderboardPage = () => {
  const isInsideMondialiShell = useIsInsideMondialiShell();
  const { weekNumber: weekNumberParam } = useParams();
  const weekNumber = Number.parseInt(weekNumberParam || '', 10);
  const now = new Date();

  const redirectPath = resolveMondialiWeekRedirectPath(weekNumber, now);
  if (redirectPath) {
    return <Navigate to={redirectPath} replace />;
  }

  const weekLabel = getMondialiWeekLabel(weekNumber);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [payload, setPayload] = useState(null);

  const selectedWindow = useMemo(
    () => getMondialiWeekWindows().find((window) => window.weekNumber === weekNumber) || null,
    [weekNumber],
  );

  useEffect(() => {
    let cancelled = false;

    const loadLeaderboard = async () => {
      if (!selectedWindow) {
        setError(true);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(false);
      try {
        const startAt = new Date(selectedWindow.startTimestamp).toISOString();
        const endAt = new Date(selectedWindow.endTimestamp + 1000).toISOString();
        const response = await getMondialiLeaderboardForRange({ startAt, endAt });
        if (!cancelled) {
          setPayload(response);
        }
      } catch (_) {
        if (!cancelled) {
          setError(true);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadLeaderboard();

    return () => {
      cancelled = true;
    };
  }, [selectedWindow]);

  const content = (
    <LeaderboardCard
      title={weekLabel}
      isLoading={isLoading}
      error={error}
      errorMessage="Impossibile caricare la classifica. Riprova tra poco."
      countries={payload?.countries || []}
      podium={payload?.podium || null}
      scoreField="average_top11_stelline"
      prizesMode="weekly"
      weekNumber={weekNumber}
      detailsIdPrefix="weekly-country"
    />
  );

  if (isInsideMondialiShell) {
    return content;
  }

  return (
    <MondialiSectionPageShell
      pageTitle={`${weekLabel} | ALMA Giochini Mondiali | Giochini di Parole`}
      activePath={getMondialiWeekPath(weekNumber)}
      now={now}
    >
      {content}
    </MondialiSectionPageShell>
  );
};

const MondialiTopPlayerPage = () => {
  const isInsideMondialiShell = useIsInsideMondialiShell();
  const now = new Date();
  const redirectPath = resolveMondialiTopPlayerRedirectPath(now);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [payload, setPayload] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const loadTopPlayerLeaderboard = async () => {
      setIsLoading(true);
      setError(false);
      await loadTopPlayerLeaderboardPayload({
        startAt: undefined,
        endAt: undefined,
        isDemoMode: false,
        onSuccess: (response) => {
          if (!cancelled) {
            setPayload(response);
          }
        },
        onError: () => {
          if (!cancelled) {
            setError(true);
          }
        },
        onSettled: () => {
          if (!cancelled) {
            setIsLoading(false);
          }
        },
      });
    };

    loadTopPlayerLeaderboard();

    return () => {
      cancelled = true;
    };
  }, []);

  if (redirectPath) {
    return <Navigate to={redirectPath} replace />;
  }

  const content = (
    <TopPlayerLeaderboardCard
      isLoading={isLoading}
      error={error}
      players={payload?.players || []}
      podium={payload?.podium || null}
    />
  );

  if (isInsideMondialiShell) {
    return content;
  }

  return (
    <MondialiSectionPageShell
      pageTitle="Top Player Mondiali | ALMA Giochini Mondiali | Giochini di Parole"
      activePath={MONDIALI_TOP_PLAYER_PATH}
      now={now}
    >
      {content}
    </MondialiSectionPageShell>
  );
};

const MondialiTopPlayerPreviewPage = () => {
  const now = new Date();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [payload, setPayload] = useState(null);

  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const startAtParam = useMemo(() => queryParams.get('start_at') || '', [queryParams]);
  const endAtParam = useMemo(() => queryParams.get('end_at') || '', [queryParams]);
  const isDemoMode = useMemo(() => queryParams.get('demo') === '1', [queryParams]);
  const effectiveStartAt = isDemoMode ? (startAtParam || '2026-06-14T00:00:00Z') : startAtParam;
  const effectiveEndAt = isDemoMode ? (endAtParam || '2026-07-19T00:00:00Z') : endAtParam;

  useEffect(() => {
    let cancelled = false;

    const loadPreview = async () => {
      setIsLoading(true);
      setErrorMessage('');
      setPayload(null);

      if (!effectiveStartAt || !effectiveEndAt) {
        setErrorMessage('Specifica entrambi i parametri query start_at e end_at in formato ISO-8601.');
        setIsLoading(false);
        return;
      }

      const startDate = new Date(effectiveStartAt);
      const endDate = new Date(effectiveEndAt);
      const isStartInvalid = Number.isNaN(startDate.getTime());
      const isEndInvalid = Number.isNaN(endDate.getTime());
      if (isStartInvalid || isEndInvalid) {
        setErrorMessage('Parametri non validi: usa date ISO-8601 per start_at e end_at.');
        setIsLoading(false);
        return;
      }

      if (startDate >= endDate) {
        setErrorMessage('Intervallo non valido: start_at deve essere precedente a end_at.');
        setIsLoading(false);
        return;
      }

      await loadTopPlayerLeaderboardPayload({
        startAt: startDate.toISOString(),
        endAt: endDate.toISOString(),
        isDemoMode,
        onSuccess: (response) => {
          if (!cancelled) {
            setPayload(response);
          }
        },
        onError: () => {
          if (!cancelled) {
            setErrorMessage("Impossibile caricare l'anteprima classifica individuale. Riprova tra poco.");
          }
        },
        onSettled: () => {
          if (!cancelled) {
            setIsLoading(false);
          }
        },
      });
    };

    loadPreview();

    return () => {
      cancelled = true;
    };
  }, [effectiveEndAt, effectiveStartAt, isDemoMode]);

  return (
    <MondialiSectionPageShell
      pageTitle="Anteprima Top Player Mondiali | ALMA Giochini Mondiali | Giochini di Parole"
      activePath={null}
      now={now}
    >
      <TopPlayerLeaderboardCard
        isLoading={isLoading}
        error={Boolean(errorMessage)}
        errorMessage={errorMessage}
        players={payload?.players || []}
        podium={payload?.podium || null}
      />
    </MondialiSectionPageShell>
  );
};

const MondialiRangeLeaderboardPreviewPage = () => {
  const now = new Date();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [payload, setPayload] = useState(null);

  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const startAtParam = useMemo(() => queryParams.get('start_at') || '', [queryParams]);
  const endAtParam = useMemo(() => queryParams.get('end_at') || '', [queryParams]);
  const isDemoMode = useMemo(() => queryParams.get('demo') === '1', [queryParams]);
  const effectiveStartAt = isDemoMode ? (startAtParam || '2026-06-21T00:00:00Z') : startAtParam;
  const effectiveEndAt = isDemoMode ? (endAtParam || '2026-06-28T00:00:00Z') : endAtParam;

  useEffect(() => {
    let cancelled = false;

    const loadLeaderboard = async () => {
      setIsLoading(true);
      setErrorMessage('');
      setPayload(null);

      if (isDemoMode) {
        const demoPayload = buildDemoRangeLeaderboardPayload({
          startAt: effectiveStartAt,
          endAt: effectiveEndAt,
          now: new Date(effectiveEndAt),
        });
        setPayload(demoPayload);
        setIsLoading(false);
        return;
      }

      if (!effectiveStartAt || !effectiveEndAt) {
        setErrorMessage('Specifica entrambi i parametri query start_at e end_at in formato ISO-8601.');
        setIsLoading(false);
        return;
      }

      const startDate = new Date(effectiveStartAt);
      const endDate = new Date(effectiveEndAt);
      const isStartInvalid = Number.isNaN(startDate.getTime());
      const isEndInvalid = Number.isNaN(endDate.getTime());
      if (isStartInvalid || isEndInvalid) {
        setErrorMessage('Parametri non validi: usa date ISO-8601 per start_at e end_at.');
        setIsLoading(false);
        return;
      }

      if (startDate >= endDate) {
        setErrorMessage('Intervallo non valido: start_at deve essere precedente a end_at.');
        setIsLoading(false);
        return;
      }

      try {
        const response = await getMondialiLeaderboardForRange({
          startAt: startDate.toISOString(),
          endAt: endDate.toISOString(),
        });
        if (!cancelled) {
          setPayload(response);
        }
      } catch (_) {
        if (!cancelled) {
          setErrorMessage("Impossibile caricare l'anteprima classifica. Riprova tra poco.");
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadLeaderboard();

    return () => {
      cancelled = true;
    };
  }, [effectiveEndAt, effectiveStartAt, isDemoMode]);

  return (
    <MondialiSectionPageShell
      pageTitle="Anteprima Classifica Mondiali | ALMA Giochini Mondiali | Giochini di Parole"
      activePath={null}
      now={now}
    >
      <LeaderboardCard
        title="Anteprima Classifica"
        isLoading={isLoading}
        error={Boolean(errorMessage)}
        errorMessage={errorMessage}
        countries={payload?.countries || []}
        podium={payload?.podium || null}
        scoreField="average_top11_stelline"
        prizesMode="weekly"
        weekNumber={1}
        detailsIdPrefix="preview-country"
      />
    </MondialiSectionPageShell>
  );
};

const MondialiFinalePreviewPage = () => {
  const now = new Date();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [payload, setPayload] = useState(null);

  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const startAtParam = useMemo(() => queryParams.get('start_at') || '', [queryParams]);
  const endAtParam = useMemo(() => queryParams.get('end_at') || '', [queryParams]);
  const isDemoMode = useMemo(() => queryParams.get('demo') === '1', [queryParams]);
  const effectiveStartAt = isDemoMode ? (startAtParam || '2026-06-14T00:00:00Z') : startAtParam;
  const effectiveEndAt = isDemoMode ? (endAtParam || '2026-06-28T00:00:00Z') : endAtParam;

  useEffect(() => {
    let cancelled = false;

    const loadPreview = async () => {
      setIsLoading(true);
      setErrorMessage('');
      setPayload(null);

      if (!effectiveStartAt || !effectiveEndAt) {
        setErrorMessage('Specifica entrambi i parametri query start_at e end_at in formato ISO-8601.');
        setIsLoading(false);
        return;
      }

      const startDate = new Date(effectiveStartAt);
      const endDate = new Date(effectiveEndAt);
      const isStartInvalid = Number.isNaN(startDate.getTime());
      const isEndInvalid = Number.isNaN(endDate.getTime());
      if (isStartInvalid || isEndInvalid) {
        setErrorMessage('Parametri non validi: usa date ISO-8601 per start_at e end_at.');
        setIsLoading(false);
        return;
      }

      if (startDate >= endDate) {
        setErrorMessage('Intervallo non valido: start_at deve essere precedente a end_at.');
        setIsLoading(false);
        return;
      }

      if (isDemoMode) {
        setPayload(buildDemoFinalePayload({
          startAt: startDate.toISOString(),
          endAt: endDate.toISOString(),
          now: endDate,
        }));
        setIsLoading(false);
        return;
      }

      try {
        const response = await getMondialiFinaleLeaderboard({
          startAt: startDate.toISOString(),
          endAt: endDate.toISOString(),
        });
        if (!cancelled) {
          setPayload(response);
        }
      } catch (_) {
        if (!cancelled) {
          setErrorMessage("Impossibile caricare l'anteprima classifica finale. Riprova tra poco.");
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadPreview();

    return () => {
      cancelled = true;
    };
  }, [effectiveEndAt, effectiveStartAt, isDemoMode]);

  return (
    <MondialiSectionPageShell
      pageTitle="Anteprima Finale Mondiali | ALMA Giochini Mondiali | Giochini di Parole"
      activePath={null}
      now={now}
    >
      <FinaleLeaderboardCard
        isLoading={isLoading}
        error={Boolean(errorMessage)}
        errorMessage={errorMessage}
        countries={payload?.countries || []}
        podium={payload?.podium || null}
        teamDetails={payload?.team_details || []}
        stacanovistiPodium={payload?.stacanovisti_podium || null}
      />
    </MondialiSectionPageShell>
  );
};

const MondialiFinalePage = () => {
  const isInsideMondialiShell = useIsInsideMondialiShell();
  const now = new Date();
  const redirectPath = resolveMondialiFinaleRedirectPath(now);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [payload, setPayload] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const loadFinale = async () => {
      setIsLoading(true);
      setError(false);
      try {
        const response = await getMondialiFinaleLeaderboard();
        if (!cancelled) {
          setPayload(response);
        }
      } catch (_) {
        if (!cancelled) {
          setError(true);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadFinale();

    return () => {
      cancelled = true;
    };
  }, []);

  const countries = useMemo(
    () => (payload?.countries || []).map((country) => ({ ...country, players: country.players || [] })),
    [payload?.countries],
  );

  if (redirectPath) {
    return <Navigate to={redirectPath} replace />;
  }

  const content = (
    <FinaleLeaderboardCard
      isLoading={isLoading}
      error={error}
      countries={countries}
      podium={payload?.podium || null}
      teamDetails={payload?.team_details || []}
      stacanovistiPodium={payload?.stacanovisti_podium || null}
    />
  );

  if (isInsideMondialiShell) {
    return content;
  }

  return (
    <MondialiSectionPageShell
      pageTitle="Finale Mondiali | ALMA Giochini Mondiali | Giochini di Parole"
      activePath={MONDIALI_FINALE_PATH}
      now={now}
    >
      {content}
    </MondialiSectionPageShell>
  );
};

export { MondialiWeeklyLeaderboardPage, MondialiFinalePage, MondialiFinalePreviewPage, MondialiRangeLeaderboardPreviewPage, MondialiTopPlayerPage, MondialiTopPlayerPreviewPage };
